import React, { useState } from 'react';
import { useWeb3 } from '../../../contexts/Web3Context';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const WalletConnectionPanel = () => {
  const { 
    walletAddress, 
    chainId, 
    provider, 
    balance, 
    isConnecting, 
    error, 
    isConnected,
    connectWallet,
    disconnectWallet,
    updateBalance
  } = useWeb3();

  const [showDetails, setShowDetails] = useState(false);

  const getNetworkName = (chainId) => {
    const networks = {
      1: 'Ethereum Mainnet',
      5: 'Goerli Testnet',
      11155111: 'Sepolia Testnet',
      137: 'Polygon Mainnet',
      80001: 'Mumbai Testnet',
      56: 'BSC Mainnet',
      97: 'BSC Testnet'
    };
    return networks?.[chainId] || `Chain ID: ${chainId}`;
  };

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address?.slice(0, 6)}...${address?.slice(-4)}`;
  };

  const handleConnect = async () => {
    const result = await connectWallet();
    if (result?.success) {
      setShowDetails(true);
    }
  };

  const handleDisconnect = async () => {
    await disconnectWallet();
    setShowDetails(false);
  };

  if (!isConnected) {
    return (
      <div className="card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="Wallet" size={24} className="text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Connect Blockchain Wallet
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Connect your MetaMask or other Web3 wallet to access real blockchain features
            </p>
            {error && (
              <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
                  <p className="text-sm text-error">{error}</p>
                </div>
              </div>
            )}
            <Button
              variant="primary"
              iconName="Wallet"
              iconPosition="left"
              onClick={handleConnect}
              disabled={isConnecting}
            >
              {isConnecting ? 'Connecting...' : 'Connect Wallet'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
            <Icon name="CheckCircle" size={24} className="text-success" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">
              Wallet Connected
            </h3>
            <p className="text-sm text-muted-foreground">
              {provider} • {getNetworkName(chainId)}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-primary hover:underline text-sm"
        >
          {showDetails ? 'Hide Details' : 'Show Details'}
        </button>
      </div>

      {showDetails && (
        <div className="space-y-4 pt-4 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Wallet Address</p>
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono text-foreground">
                  {formatAddress(walletAddress)}
                </code>
                <button
                  onClick={() => navigator?.clipboard?.writeText(walletAddress)}
                  className="text-primary hover:text-primary/80"
                  title="Copy address"
                >
                  <Icon name="Copy" size={14} />
                </button>
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">ETH Balance</p>
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-foreground">
                  {balance !== null ? `${balance?.toFixed(4)} ETH` : 'Loading...'}
                </p>
                <button
                  onClick={updateBalance}
                  className="text-primary hover:text-primary/80"
                  title="Refresh balance"
                >
                  <Icon name="RefreshCw" size={14} />
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ExternalLink"
              iconPosition="right"
              onClick={() => window?.open(`https://etherscan.io/address/${walletAddress}`, '_blank')}
            >
              View on Etherscan
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="LogOut"
              iconPosition="left"
              onClick={handleDisconnect}
            >
              Disconnect
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletConnectionPanel;